import { isCancel } from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeSpaceNav } from '@app/actions/navigation';
import { selectSpaceSelector } from '@core/spaceSelector/operations';
import { fetchJoinedClassRoomsSuccess } from '@core/actions/classRoom';
import {
  Accordion,
  Area,
  Box,
  Icons,
  Skeleton,
  Text,
  TextField,
  useTheme,
} from '@ublend-npm/aulaui-core';

import SpaceList from './SpaceList';
import { Space } from './types';
import { spacesSearch } from '@core/api/search';
import { AulaState } from '@core/types/state';
import { getSpaceById } from '@core/selectors/space';
import getSpace from '../../../../../../core/api/spaces/getSpace';

const PAGE_SIZE = 20;

const isUserAssignedSpace = (state: any, spaceId: string): boolean => {
  const space = getSpaceById(state, spaceId);
  // Space found in user's spaces means it's assigned to them
  return !!space;
};

export const StaffSpaces = ({ showArchived = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [spaces, setSpaces] = useState<Space[]>([]);
  // Get all states from store
  const state = useSelector((state: any) => state);

  const theme = useTheme();
  const dispatch = useDispatch();

  const handleToggleExpanded = () => {
    setExpanded((p) => !p);
  };

  const handleSpaceSelect = async (spaceId: string) => {
    // Dispatch loading state
    dispatch({
      type: 'SPACE_SELECT_LOADING',
      payload: { isSpaceLoading: true },
    });

    try {
      // Check if user is already assigned to this space
      const isSpaceAlreadyUserAsssigned = isUserAssignedSpace(state, spaceId);
      if (!isSpaceAlreadyUserAsssigned) {
        // Fetch space details from getSpace
        const space = await getSpace({ spaceId: spaceId });
        if (space) {
          fetchJoinedClassRoomsSuccess(
            [
              {
                objectId: space?.id,
                classRoom: {
                  objectId: space?.id,
                  name: space?.name,
                  avatar: space?.avatar ? space.avatar : '',
                  isStaff: true,
                  archived: space?.archived,
                  importantPostCount: space?.importantPostCount || 0,
                  participantCount: space?.participantCount || 0,
                  studentCount: space?.studentCount || 0,
                  startDate: space?.startDate,
                  endDate: space?.endDate,
                  instructors: space?.educators,
                },
              },
            ],
            dispatch
          );
        }
      }
      // Set flag to indicate navigation from staff access (for mobile reload)
      sessionStorage.setItem('navigatingFromStaffAccess', 'true');
      sessionStorage.setItem('staffAccessSpaceId', spaceId);
      dispatch(changeSpaceNav(spaceId));
      dispatch(selectSpaceSelector(spaceId));
    } finally {
      // Set the loading as false
      dispatch({
        type: 'SPACE_SELECT_LOADING',
        payload: { isSpaceLoading: false },
      });
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const prevResultsLength = useRef(0);

  useEffect(() => {
    setSpaces([]);
    prevResultsLength.current = 0;
    if (!searchText) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await spacesSearch({
          query: searchText,
          skip: 0,
          pageSize: PAGE_SIZE,
          showArchived,
        });
        const fetchedSpaces = res.data.spaces || [];
        setSpaces(fetchedSpaces);
        setLoading(false);
      } catch (error) {
        if (!isCancel(error)) {
          setLoading(false);
        }
      }
    })();
  }, [searchText, showArchived]);

  useEffect(() => {
    const currResultsLength = spaces.length;

    if (
      loading ||
      currResultsLength === 0 ||
      prevResultsLength.current + PAGE_SIZE > currResultsLength
    ) {
      return;
    } else {
      prevResultsLength.current = currResultsLength;
    }

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setLoading(true);
        (async () => {
          try {
            const res = await spacesSearch({
              query: searchText,
              skip: currResultsLength,
              pageSize: PAGE_SIZE,
              showArchived,
            });
            const newSpaces = res.data.spaces || [];
            setSpaces((cur) => [...cur, ...newSpaces]);
            setLoading(false);
          } catch (error) {
            if (!isCancel(error)) {
              return;
            }
          }
        })();
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [loading, searchText, showArchived]);

  const isStaff = useSelector((state: AulaState) =>
    state.user.user?.userRoles.includes('staff')
  );

  if (!isStaff) {
    return null;
  }

  return (
    <Accordion
      label="Spaces with staff access"
      expanded={expanded}
      onChange={handleToggleExpanded}
      leftAligner
      size="large"
      sx={{
        bgcolor: 'transparent',
        '& .start-summary .MuiTypography-subtitle2': {
          fontSize: '20px',
          fontWeight: 600,
        },
        '& .MuiAccordionDetails-root > .MuiBox-root': {
          cursor: 'auto',
          overflow: 'hidden',
          maxHeight: '30rem',
        },
      }}
    >
      <TextField
        label="Search by name"
        value={searchText}
        leftElement={<Icons.Search />}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            backgroundColor: theme.palette.background.paper,
          },
        }}
      />
      <Box ref={containerRef} sx={{ overflowY: 'auto', maxHeight: '20rem' }}>
        {!loading && !searchText && (
          <Area
            sx={{
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <Text kind="p1" fontWeight="600">
              Staff access for spaces
            </Text>
            <Text kind="p2" sx={{ color: theme.palette.text.secondary }}>
              As a staff member, you can navigate all the spaces in the
              institution. Type the name of the desired space on the search box
              above.
            </Text>
          </Area>
        )}
        {!loading && searchText && spaces.length === 0 && (
          <Area
            sx={{
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <Text kind="p1" fontWeight="600">
              No matches found
            </Text>
            <Text kind="p2" sx={{ color: theme.palette.text.secondary }}>
              Your search did not return any results. Check your spelling and
              try again.
            </Text>
          </Area>
        )}
        {searchText && spaces.length > 0 && (
          <SpaceList spaces={spaces} onClick={handleSpaceSelect} />
        )}
        {loading &&
          Array.from(Array(3), (_, i) => i + 1).map((key) => (
            <Skeleton
              key={key}
              role="status"
              kind="rounded"
              height={56}
              sx={{ marginBottom: '0.5rem' }}
            />
          ))}
      </Box>
    </Accordion>
  );
};
